import { isNativeApp, requestApp, WEB_TO_APP } from 'common/webview';
import i18n from 'i18next';

// ─── 공유 데이터 인터페이스 ───

export interface ShareParams {
    title: string;
    text: string;
    url: string;
}

export interface MeetingShareData {
    meetingName: string;
    date?: string;
    time?: string;
    location?: string;
    inviteCode: string;
    description?: string;
}

export interface CostShareData {
    meetingName: string;
    totalAmount: number;
    perPerson: number;
    shareText: string;
    meetingId: string;
}

// ─── 공유 URL 생성 ───

const getShareBaseUrl = () => window.location.origin;

export const getMeetingShareUrl = (inviteCode: string) =>
    `${getShareBaseUrl()}/invitation/${inviteCode}`;

export const getCostShareUrl = (meetingId: string) =>
    `${getShareBaseUrl()}/cost/public/${meetingId}`;

// ─── OS 기본 공유 시트 ───

/**
 * OS 기본 공유하기 창을 띄움
 * - 네이티브 앱: WebView 브릿지 → RN의 Share.share() 호출
 * - 모바일 웹: Web Share API (navigator.share)
 * - 데스크톱 웹: 클립보드 복사 fallback
 *
 * @returns 공유 성공 여부 (fallback 복사 포함)
 */
export const openShare = async (params: ShareParams): Promise<boolean> => {
    // 1) 네이티브 앱 → 브릿지로 위임
    if (isNativeApp()) {
        requestApp(WEB_TO_APP.SHARE, {
            title: params.title,
            message: params.text,
            url: params.url,
        });
        return true;
    }

    // 2) 모바일 웹 → Web Share API
    if (navigator.share) {
        try {
            await navigator.share({
                title: params.title,
                text: params.text,
                url: params.url,
            });
            return true;
        } catch (e: any) {
            // 사용자가 취소한 경우
            if (e.name === 'AbortError') return false;
        }
    }

    // 3) fallback → 클립보드 복사
    return copyToClipboard(`${params.text}\n${params.url}`);
};

// ─── 모임 초대 공유 ───

export const shareMeeting = (data: MeetingShareData): Promise<boolean> => {
    const url = getMeetingShareUrl(data.inviteCode);
    const text = buildMeetingShareText(data);

    // 온보딩 체크리스트: 친구 초대 완료 플래그
    try {
        const userNo = sessionStorage.getItem('userNo');
        if (userNo) localStorage.setItem(`invited_${userNo}`, '1');
    } catch {}

    return openShare({
        title: `${data.meetingName} 모임 초대`,
        text,
        url,
    });
};

// ─── 정산 결과 공유 ───

export const shareCost = (data: CostShareData): Promise<boolean> => {
    const url = getCostShareUrl(data.meetingId);

    return openShare({
        title: `${data.meetingName} 정산 결과`,
        text: data.shareText,
        url,
    });
};

// ─── 클립보드 복사 ───

export const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const result = document.execCommand('copy');
        document.body.removeChild(textarea);
        return result;
    }
};

// ─── 텍스트 빌더 ───

const pickRandom = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const tArr = (key: string): string[] => {
    const v = i18n.t(key, { returnObjects: true }) as any;
    return Array.isArray(v) ? v : [];
};

const buildMeetingShareText = (data: MeetingShareData): string => {
    // 감성적인 인트로 (랜덤) — locale에서 가져옴
    const intros = tArr('tone.shareMeeting.intro');
    const closings = tArr('tone.shareMeeting.closing');

    let intro = intros.length > 0
        ? pickRandom(intros).replace(/\{\{name\}\}/g, data.meetingName)
        : `"${data.meetingName}"`;

    let text = intro;
    if (data.date) text += `\n${data.date}`;
    if (data.time) text += `\n${data.time}`;
    if (data.location) text += `\n${data.location}`;
    text += `\n\n${data.inviteCode}`;
    if (closings.length > 0) text += pickRandom(closings);
    return text;
};
